import { Link, createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import {
  BellOff,
  BellRing,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  CalendarPlus,
  Clock,
  Inbox,
  MessageSquarePlus,
  Plug,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, Field, SelectField, Stat } from "@/components/crm/parts";
import { supabase } from "@/integrations/supabase/client";
import { useAccounts, useCrmInvalidate, useOrgActivity, useQbrs } from "@/lib/crm/useCrm";
import { useOrg } from "@/lib/crm/useOrg";
import { useReminderPrefs } from "@/lib/crm/useReminderPrefs";
import { useInboxDemoFeed } from "@/lib/crm/useInboxDemo";
import {
  isMuted,
  muteKey,
  reminderSignal,
  remindersFor,
  thresholdsOf,
  todayIso,
  type Reminder,
  type ReminderMute,
  type ReminderPrefs,
} from "@/lib/crm/engagement";
import { useReminderMutes, type MuteTarget } from "@/lib/crm/useReminderMutes";
import { ReminderHistoryPanel } from "@/components/crm/ReminderHistoryPanel";

import {
  customerResolver,
  isMeetingLike,
  useCustomerWork,
  type AccountRef,
} from "@/lib/crm/customer-work";
import { round1 } from "@/lib/hub";
import { EngagementTimeline } from "@/components/crm/EngagementTimeline";
import { buildEngagementTimeline, type TimelineActivity } from "@/lib/crm/timeline";
import { HEALTH_BANDS, scoreEngagement, type EngagementHealth } from "@/lib/crm/health";


export const Route = createFileRoute("/_authenticated/sales/engagement")({
  head: () => ({
    meta: [
      { title: "Customer engagement tracker — MyPriorities" },
      {
        name: "description",
        content:
          "Track meetings, tasks, hours and contact history per customer, with reminders for quiet accounts and upcoming QBRs.",
      },
      { property: "og:title", content: "Customer engagement tracker — MyPriorities" },
      {
        property: "og:description",
        content:
          "See how engaged every customer is: last contact, meetings held, hours spent and QBRs due.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: EngagementPage,
});

function EngagementPage() {
  return (
    <SalesShell
      section="customers"
      title="Customer engagement"
      subtitle="How much attention each customer is actually getting — meetings, tasks, hours and contact history."
    >
      {({ orgId, userId }) => <Engagement orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

const CONTACT_KINDS = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "message", label: "Message (Slack / Teams)" },
  { value: "meeting", label: "Meeting" },
  { value: "note", label: "Other touchpoint" },
] as const;

const SORTS = [
  { value: "health", label: "Lowest health first" },
  { value: "health-desc", label: "Highest health first" },
  { value: "quiet", label: "Quietest first" },
  { value: "hours", label: "Most hours" },
  { value: "meetings", label: "Most meetings" },
  { value: "name", label: "Name" },
] as const;

const HEALTH_FILTERS = [
  { value: "all", label: "All health bands" },
  ...HEALTH_BANDS.map((b) => ({ value: b.value, label: b.label })),
] as const;


const today = () => new Date().toISOString().slice(0, 10);

function daysBetween(from: string, to: string) {
  const a = new Date(`${from.slice(0, 10)}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86_400_000);
}

type Row = {
  account: AccountRef;
  lastContactOn: string | null;
  lastContactLabel: string;
  daysSince: number | null;
  meetings90: number;
  hours90: number;
  hoursTotal: number;
  openTasks: number;
  nextQbrOn: string | null;
  qbrDueInDays: number | null;
  health: EngagementHealth;
};

function Engagement({ orgId, userId }: { orgId: string; userId: string }) {
  const accounts = useAccounts(orgId);
  const qbrs = useQbrs(orgId);
  const activity = useOrgActivity(orgId);
  const work = useCustomerWork(orgId);
  const inbox = useInboxDemoFeed(orgId, userId);
  const invalidate = useCrmInvalidate();
  const org = useOrg();
  const { prefs } = useReminderPrefs();
  const { mutes, setMute, clearMute } = useReminderMutes(orgId);

  const thresholds = useMemo(
    () => thresholdsOf(org.data?.organisation ?? null),
    [org.data?.organisation],
  );

  const [sort, setSort] = useState<string>("health");
  const [healthFilter, setHealthFilter] = useState<string>("all");
  const [view, setView] = useState<"table" | "timeline">("table");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [logFor, setLogFor] = useState<AccountRef | null>(null);




  const refs = useMemo<AccountRef[]>(
    () => (accounts.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accounts.data],
  );
  const accountNames = useMemo(
    () => new Map(refs.map((a) => [a.id, a.name])),
    [refs],
  );
  const ownerByAccount = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a.owner_user_id ?? userId])),
    [accounts.data, userId],
  );
  const resolver = useMemo(() => customerResolver(refs, work.links), [refs, work.links]);

  const rows = useMemo<Row[]>(() => {
    const now = today();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const cutoffKey = cutoff.toISOString().slice(0, 10);

    const base = new Map<string, Omit<Row, "health">>();
    for (const account of refs) {
      base.set(account.id, {
        account,
        lastContactOn: null,
        lastContactLabel: "",
        daysSince: null,
        meetings90: 0,
        hours90: 0,
        hoursTotal: 0,
        openTasks: 0,
        nextQbrOn: null,
        qbrDueInDays: null,
      });
    }

    const touch = (id: string, on: string, label: string) => {
      const row = base.get(id);
      if (!row) return;
      if (!row.lastContactOn || on > row.lastContactOn) {
        row.lastContactOn = on;
        row.lastContactLabel = label;
      }
    };

    for (const done of work.completions) {
      const on = done.completed_on.slice(0, 10);
      const hours = Number(done.hours || 0);
      for (const account of resolver.accountsFor(done)) {
        const row = base.get(account.id);
        if (!row) continue;
        row.hoursTotal = round1(row.hoursTotal + hours);
        if (on >= cutoffKey) row.hours90 = round1(row.hours90 + hours);
        if (isMeetingLike(done)) {
          if (on >= cutoffKey) row.meetings90 += 1;
          touch(account.id, on, "meeting");
        }
      }
    }

    for (const task of work.tasks) {
      if (task.done) continue;
      for (const account of resolver.accountsFor(task)) {
        const row = base.get(account.id);
        if (row) row.openTasks += 1;
      }
    }

    for (const line of activity.data ?? []) {
      if (line.record_kind !== "account") continue;
      if (!["contact", "call", "email", "message", "meeting", "note"].includes(line.kind)) continue;
      touch(line.record_id, line.created_at.slice(0, 10), line.kind === "contact" ? "logged contact" : line.kind);
    }

    for (const qbr of qbrs.data ?? []) {
      const row = base.get(qbr.account_id);
      if (!row || !qbr.next_due_on) continue;
      if (!row.nextQbrOn || qbr.next_due_on < row.nextQbrOn) {
        row.nextQbrOn = qbr.next_due_on;
        row.qbrDueInDays = daysBetween(now, qbr.next_due_on);
      }
      if (qbr.last_held_on) touch(qbr.account_id, qbr.last_held_on, "QBR");
    }

    const list = [...base.values()].map<Row>((row) => {
      const daysSince = row.lastContactOn ? daysBetween(row.lastContactOn, now) : null;
      return {
        ...row,
        daysSince,
        health: scoreEngagement({
          daysSince,
          meetings90: row.meetings90,
          hours90: row.hours90,
          qbrDueInDays: row.qbrDueInDays,
          nextQbrOn: row.nextQbrOn,
          quietDays: thresholds.quietDays,
          qbrLeadDays: thresholds.qbrLeadDays,
        }),
      };
    });

    const byQuiet = (a: Row, b: Row) => (b.daysSince ?? 9999) - (a.daysSince ?? 9999);
    if (sort === "hours") list.sort((a, b) => b.hoursTotal - a.hoursTotal);
    else if (sort === "meetings") list.sort((a, b) => b.meetings90 - a.meetings90);
    else if (sort === "name") list.sort((a, b) => a.account.name.localeCompare(b.account.name));
    else if (sort === "health") list.sort((a, b) => a.health.score - b.health.score);
    else if (sort === "health-desc") list.sort((a, b) => b.health.score - a.health.score);
    else list.sort(byQuiet);
    return list;
  }, [
    refs,
    resolver,
    work.completions,
    work.tasks,
    activity.data,
    qbrs.data,
    sort,
    thresholds,
  ]);

  const visibleRows = useMemo(
    () => (healthFilter === "all" ? rows : rows.filter((r) => r.health.band === healthFilter)),
    [rows, healthFilter],
  );


  // Only the visible timelines are derived: the expanded row, or every row in
  // timeline view.
  const timelineIds = useMemo(() => {
    if (view === "timeline") return visibleRows.map((r) => r.account.id);
    return expanded ? [expanded] : [];
  }, [view, visibleRows, expanded]);


  const timelines = useMemo(() => {
    const lines = (activity.data ?? []) as unknown as TimelineActivity[];
    const map = new Map<string, ReturnType<typeof buildEngagementTimeline>>();
    for (const accountId of timelineIds) {
      map.set(
        accountId,
        buildEngagementTimeline({
          accountId,
          completions: work.completions,
          tasks: work.tasks,
          activity: lines,
          resolver,
        }),
      );
    }
    return map;
  }, [timelineIds, work.completions, work.tasks, activity.data, resolver]);


  const allReminders = useMemo<ReminderEntry[]>(
    () =>
      rows.flatMap((row) => {
        const snapshot = {
          accountId: row.account.id,
          accountName: row.account.name,
          lastContactOn: row.lastContactOn,
          daysSince: row.daysSince,
          nextQbrOn: row.nextQbrOn,
          qbrDueInDays: row.qbrDueInDays,
        };
        return remindersFor(snapshot, thresholds).map((reminder) => ({
          reminder,
          target: {
            accountId: reminder.accountId,
            ownerUserId: ownerByAccount.get(row.account.id) ?? userId,
            reason: reminder.reason,
            signal: reminderSignal(snapshot, reminder.reason),
          } satisfies MuteTarget,
        }));
      }),
    [rows, thresholds],
  );

  const todayKey = todayIso();
  const { active: reminders, muted: mutedReminders } = useMemo(() => {
    const active: ReminderEntry[] = [];
    const muted: Array<ReminderEntry & { mute: ReminderMute }> = [];
    for (const entry of allReminders) {
      const mute = mutes.get(muteKey(entry.reminder.accountId, entry.reminder.reason));
      if (mute && isMuted(mute, entry.target.signal, todayKey)) muted.push({ ...entry, mute });
      else active.push(entry);
    }
    return { active, muted };
  }, [allReminders, mutes, todayKey]);


  const engaged = rows.filter((r) => (r.daysSince ?? 9999) <= 30).length;
  const quiet = rows.filter((r) => (r.daysSince ?? 9999) >= thresholds.quietDays).length;
  const qbrsDue = rows.filter(
    (r) => r.qbrDueInDays !== null && r.qbrDueInDays <= thresholds.qbrLeadDays,
  ).length;
  const hours90 = round1(rows.reduce((sum, r) => sum + r.hours90, 0));
  const avgHealth = rows.length
    ? Math.round(rows.reduce((sum, r) => sum + r.health.score, 0) / rows.length)
    : 0;
  const needAttention = rows.filter(
    (r) => r.health.band === "risk" || r.health.band === "critical",
  ).length;

  if (accounts.isLoading || work.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading engagement…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat
          label="Avg health score"
          value={String(avgHealth)}
          hint={`${needAttention} at risk or critical`}
        />
        <Stat label="Engaged (30 days)" value={String(engaged)} hint={`of ${rows.length} accounts`} />
        <Stat
          label="Going quiet"
          value={String(quiet)}
          hint={`No contact in ${thresholds.quietDays}+ days`}
        />
        <Stat
          label="QBRs due"
          value={String(qbrsDue)}
          hint={`Next ${thresholds.qbrLeadDays} days`}
        />
        <Stat label="Hours (90 days)" value={`${hours90}h`} />
      </div>


      <RemindersPanel
        reminders={reminders}
        muted={mutedReminders}
        prefs={prefs}
        busy={setMute.isPending || clearMute.isPending}
        onLog={(accountId) => {
          const account = refs.find((a) => a.id === accountId);
          if (account) setLogFor(account);
        }}
        onSnooze={(target, days) => {
          setMute.mutate(
            { target, days },
            {
              onSuccess: () =>
                toast.success(days === null ? "Reminder dismissed" : `Snoozed for ${days} days`),
              onError: (error) => toast.error((error as Error).message),
            },
          );
        }}
        onRestore={(accountId, reason) => {
          clearMute.mutate(
            { accountId, reason },
            {
              onSuccess: () => toast.success("Reminder restored"),
              onError: (error) => toast.error((error as Error).message),
            },
          );
        }}
      />

      <ReminderHistoryPanel orgId={orgId} userId={userId} accountNames={accountNames} />



      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48">
            <SelectField label="Sort by" value={sort} onChange={setSort} options={SORTS} />
          </div>
          <div className="w-48">
            <SelectField
              label="Health"
              value={healthFilter}
              onChange={setHealthFilter}
              options={HEALTH_FILTERS}
            />
          </div>
          <p className="pb-2 text-xs text-muted-foreground">
            Showing {visibleRows.length} of {rows.length}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant={view === "table" ? "default" : "outline"}
            onClick={() => setView("table")}
          >
            Table
          </Button>
          <Button
            size="sm"
            variant={view === "timeline" ? "default" : "outline"}
            onClick={() => setView("timeline")}
          >
            Timeline
          </Button>
        </div>
      </div>


      <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <Plug className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div className="text-sm">
            <p className="font-medium">Log touchpoints automatically (Enterprise)</p>
            <p className="text-muted-foreground">
              Gmail, Outlook, Slack and Teams capture calls, emails and meetings for you — review and
              log them in the inbox, or log contacts here in a couple of clicks.
            </p>
          </div>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link to="/sales/inbox">
            <Inbox className="mr-1.5 h-3.5 w-3.5" />
            Review inbox{inbox.pending.length ? ` (${inbox.pending.length})` : ""}
          </Link>
        </Button>
        <Button asChild size="sm" variant="ghost">
          <Link to="/account" hash="calendar-feed-url">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
            Subscribe in your calendar
          </Link>
        </Button>
      </div>


      {rows.length === 0 ? (
        <EmptyState
          title="No customer accounts yet"
          body="Add accounts in Customers → Accounts and engagement will start building here."
        />
      ) : visibleRows.length === 0 ? (
        <EmptyState
          title="No accounts in that health band"
          body="Change the health filter to see more customers."
        />
      ) : view === "timeline" ? (
        <div className="space-y-4">
          {visibleRows.map((row) => {
            const timeline = timelines.get(row.account.id);
            return timeline ? (
              <EngagementTimeline
                key={row.account.id}
                accountName={row.account.name}
                timeline={timeline}
                onLog={() => setLogFor(row.account)}
              />
            ) : null;
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Health</th>
                <th className="px-3 py-2 font-medium">Last contact</th>
                <th className="px-3 py-2 text-right font-medium">Meetings (90d)</th>
                <th className="px-3 py-2 text-right font-medium">Hours (90d)</th>
                <th className="px-3 py-2 text-right font-medium">Hours total</th>
                <th className="px-3 py-2 text-right font-medium">Open tasks</th>
                <th className="px-3 py-2 font-medium">Next QBR</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {

                const days = row.daysSince;
                const warn = Math.round(thresholds.quietDays / 2);
                const tone =
                  days === null || days >= thresholds.quietDays
                    ? "destructive"
                    : days >= warn
                      ? "secondary"
                      : "outline";
                const open = expanded === row.account.id;

                return (
                  <Fragment key={row.account.id}>
                  <tr className="border-t border-border">
                    <td className="px-3 py-2 font-medium">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-left hover:underline"
                        aria-expanded={open}
                        onClick={() => setExpanded(open ? null : row.account.id)}
                      >
                        {open ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        {row.account.name}
                      </button>
                    </td>
                    <td className="px-3 py-2" title={row.health.reason}>
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums font-medium">{row.health.score}</span>
                        <Badge variant={row.health.tone}>{row.health.bandLabel}</Badge>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{row.health.reason}</p>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">

                      {row.lastContactOn
                        ? `${row.lastContactOn} · ${row.lastContactLabel}`
                        : "No contact logged"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.meetings90}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.hours90}h</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.hoursTotal}h</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.openTasks}</td>
                    <td className="px-3 py-2">
                      {row.nextQbrOn ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <CalendarClock className="h-3.5 w-3.5" />
                          {row.nextQbrOn}
                          {row.qbrDueInDays !== null &&
                          row.qbrDueInDays <= thresholds.qbrLeadDays ? (

                            <Badge variant="secondary" className="ml-1">
                              {row.qbrDueInDays < 0 ? "overdue" : `in ${row.qbrDueInDays}d`}
                            </Badge>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not scheduled</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={tone}>
                        {days === null
                          ? "Never contacted"
                          : days >= thresholds.quietDays
                            ? `Contact overdue (${days}d)`
                            : days >= warn
                              ? `Due a check-in (${days}d)`
                              : `Engaged (${days}d)`}

                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setLogFor(row.account)}>
                        <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />
                        Log contact
                      </Button>
                    </td>
                  </tr>
                  {open && timelines.get(row.account.id) ? (
                    <tr className="border-t border-border bg-muted/10">
                      <td colSpan={10} className="p-3">
                        <EngagementTimeline
                          accountName={row.account.name}
                          timeline={timelines.get(row.account.id)!}
                          onLog={() => setLogFor(row.account)}
                        />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                );
              })}

            </tbody>
          </table>
        </div>
      )}

      <LogContactDialog
        account={logFor}
        orgId={orgId}
        userId={userId}
        onClose={() => setLogFor(null)}
        onSaved={() => {
          invalidate();
          setLogFor(null);
        }}
      />
    </div>
  );
}

type ReminderEntry = { reminder: Reminder; target: MuteTarget };

const SNOOZE_OPTIONS = [7, 14, 30];

/** Accounts that need a nudge right now — same rules the daily reminders use. */
function RemindersPanel({
  reminders,
  muted,
  prefs,
  busy,
  onLog,
  onSnooze,
  onRestore,
}: {
  reminders: ReminderEntry[];
  muted: Array<ReminderEntry & { mute: ReminderMute }>;
  prefs: ReminderPrefs;
  busy: boolean;
  onLog: (accountId: string) => void;
  onSnooze: (target: MuteTarget, days: number | null) => void;
  onRestore: (accountId: string, reason: Reminder["reason"]) => void;
}) {
  const frequency =
    prefs.frequency === "off"
      ? "Notifications are off — change this in Account → Customer reminders."
      : `You'll also get ${prefs.frequency} bell notifications for these.`;

  const mutedList = muted.length ? (
    <div className="rounded-lg border border-dashed border-border bg-muted/30">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <BellOff className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-medium">Paused nudges ({muted.length})</p>
      </div>
      <ul className="divide-y divide-border">
        {muted.map(({ reminder, mute }) => (
          <li
            key={`${reminder.accountId}-${reminder.reason}`}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm"
          >
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{reminder.accountName}</span>
              <Badge variant="outline">{reminder.reason === "quiet" ? "Quiet" : "QBR"}</Badge>
              <span className="text-xs text-muted-foreground">
                {mute.mutedUntil
                  ? `Snoozed until ${mute.mutedUntil}`
                  : "Dismissed until this changes"}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onRestore(reminder.accountId, reminder.reason)}
            >
              Restore
            </Button>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  if (!reminders.length) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-border bg-card p-4 text-sm">
          <p className="font-medium">Nothing needs chasing</p>
          <p className="text-muted-foreground">
            Every customer has been contacted recently and no QBR is due inside your window.
          </p>
        </div>
        {mutedList}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-muted-foreground" />
            <p className="font-medium">Needs attention ({reminders.length})</p>
          </div>
          <p className="text-xs text-muted-foreground">{frequency}</p>
        </div>
        <ul className="divide-y divide-border">
          {reminders.map(({ reminder, target }) => (
            <li
              key={`${reminder.accountId}-${reminder.reason}`}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-sm"
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium">{reminder.accountName}</span>
                <Badge variant={reminder.reason === "quiet" ? "destructive" : "secondary"}>
                  {reminder.reason === "quiet" ? "Quiet" : "QBR"}
                </Badge>
                <span className="text-muted-foreground">{reminder.detail}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => onLog(reminder.accountId)}>
                  <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />
                  Log contact
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" disabled={busy}>
                      <Clock className="mr-1.5 h-3.5 w-3.5" />
                      Snooze
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {SNOOZE_OPTIONS.map((days) => (
                      <DropdownMenuItem key={days} onClick={() => onSnooze(target, days)}>
                        For {days} days
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem onClick={() => onSnooze(target, null)}>
                      Dismiss until this changes
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {mutedList}
    </div>
  );
}


function LogContactDialog({

  account,
  orgId,
  userId,
  onClose,
  onSaved,
}: {
  account: AccountRef | null;
  orgId: string;
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<string>("call");
  const [on, setOn] = useState<string>(today());
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!account) return;
    setSaving(true);
    const label = CONTACT_KINDS.find((k) => k.value === kind)?.label ?? "Contact";
    // Back-date the activity to the day the contact actually happened, so
    // last-contact, health scores and quiet-account reminders stay honest.
    const day = on > today() ? today() : on;
    const { error } = await supabase.from("crm_activity").insert({
      organisation_id: orgId,
      owner_user_id: userId,
      record_kind: "account",
      record_id: account.id,
      kind,
      created_at: `${day}T12:00:00.000Z`,
      summary: `${label} on ${day}${summary.trim() ? ` — ${summary.trim()}` : ""}`,
    });

    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Contact logged for ${account.name}`);
    setSummary("");
    onSaved();
  }

  return (
    <Dialog open={Boolean(account)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log contact{account ? ` — ${account.name}` : ""}</DialogTitle>
          <DialogDescription>
            Record a call, email or message so engagement stays accurate between meetings.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <SelectField label="Type" value={kind} onChange={setKind} options={CONTACT_KINDS} />
          <Field label="Date">
            <Input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
          </Field>
          <Field label="Notes" hint="Optional — what was discussed or agreed.">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Checked in on renewal timing"
              rows={3}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Log contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
